import React, { useMemo } from "react";
import { Pagination } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { Redirect, useHistory } from "react-router-dom";

import Preloader from "~/components/Preloader";
import CatalogFilters from "~/pages/CatalogApplications/CatalogFilters";
import useGetDataCatalogApplications from "~/pages/CatalogApplications/hook/useGetDataCatalogApplications";
import Wrapper from "~/shared/ui/Wrapper";
import { useURLParamsGetter } from "~/hooks/useURLParamsSetter";
import {
  useUpdateCandidateAuditStatus,
  useUpdateCandidateStatus,
} from "~/pages/CatalogApplications/catalogApi";
import { getCatalogApplicationsColumns } from "~/pages/CatalogApplications/model/tableAdapter";
import { CATALOG_OPTIONS } from "~/pages/CatalogApplications/options";
import { FormValues } from "~/pages/CatalogApplications/types";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";

import s from "./CatalogApplications.less";

// Filter keys managed by CatalogFilters via the URL. Everything else in the
// URL (page/count) is pagination. Used to rebuild the API query string.
const FILTER_KEYS = [
  "auditStatus",
  "status",
  "candidateType",
  "legalPersonName",
  "inn",
  "executiveFullName",
  "executiveEmail",
  "futureAddress",
  "executiveWorkPhone",
  "isCompetitive",
] as const;

const CatalogApplications = () => {
  const { t } = useTranslation();
  const urlParams = useURLParamsGetter<Record<string, string>>();
  const history = useHistory();
  const { hasPermission } = usePermissions();

  const page = Number(urlParams.page) || 0;
  const count = Number(urlParams.count) || 25;

  if (!hasPermission("candidateSelection.candidates.get")) {
    return <Redirect key={`main`} to={"/"} />;
  }

  const urlParamsString = useMemo(() => {
    const query = new URLSearchParams();
    for (const key of FILTER_KEYS) {
      const value = urlParams[key];
      if (value !== undefined && value !== null && value !== "") {
        query.append(key, String(value));
      }
    }
    query.append("order", "ASC");
    query.append("orderBy", "id");
    query.append("page", String(page));
    query.append("count", String(count));
    return query.toString();
  }, [urlParams, page, count]);

  const { data, error, isLoading } =
    useGetDataCatalogApplications(urlParamsString);

  const { mutate: updateStatus } = useUpdateCandidateStatus();
  const { mutate: updateAuditStatus } = useUpdateCandidateAuditStatus();

  const { handleCount, handlePage } = usePaginationParams("urlParams");

  const goToCatalog = (record: FormValues) =>
    history.push(`/catalogApplications/${record.id}`);

  const columns = useMemo(
    () =>
      getCatalogApplicationsColumns(
        t,
        CATALOG_OPTIONS,
        hasPermission,
        updateStatus,
        updateAuditStatus,
      ),
    [t, hasPermission, updateStatus, updateAuditStatus],
  );

  if (isLoading) {
    return <Preloader />;
  }

  if (error) {
    const message =
      error instanceof Error ? error.message : String(error ?? "unknown");
    return <div>Ошибка: {message}</div>;
  }

  return (
    <Wrapper.Content
      title={t(`catalogApplications.title`)}
      filterComponent={<CatalogFilters />}
    >
      <TableV2<FormValues>
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={data?.items ?? []}
        loading={isLoading}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => goToCatalog(record),
          style: { cursor: "pointer" },
        })}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={data?.total ?? 0}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
    </Wrapper.Content>
  );
};

export default CatalogApplications;
